import { api } from '@/lib/api';
import { env } from '@/lib/env';
import type {
  LabelFormat,
  ScannableEntityType,
  ScannedEntitySummary,
} from './types';

/**
 * Scan / QR / label REST client (Phase 5c).
 *
 * Endpoints:
 *   GET /scan/:entityType/:entityId      → ScannedEntitySummary (JSON)
 *   GET /qr/:entityType/:entityId        → SVG/PNG binary
 *      ?format=svg|png&size=64..2048
 *   GET /labels/:entityType/:entityId    → PDF binary
 *      ?format=dymo32x57|brother62x29
 *   GET /labels/formats                  → list of supported formats (JSON)
 *
 * The QR + label endpoints stream binary content; the frontend
 * doesn't fetch them through axios — it points an `<img>`/`<embed>`
 * at the URL directly so the browser handles caching and rendering.
 * The service therefore exposes URL builders rather than fetch
 * helpers for those, and a real `axios.get` only for the JSON shape.
 */

interface LabelFormatDescriptor {
  format: LabelFormat;
  widthMm: number;
  heightMm: number;
}

/**
 * Build an absolute URL against the same backend the rest of the app
 * talks to. We can't rely on a relative path because <img> / <embed>
 * resolve relative to the current document URL, which would prefix
 * the SPA route (e.g. /work-orders/abc) instead of the API root.
 */
function apiUrl(path: string): string {
  const base = env.VITE_API_URL.replace(/\/$/, '');
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

export const scanApi = {
  resolve: async (
    entityType: ScannableEntityType,
    entityId: string,
  ): Promise<ScannedEntitySummary> => {
    const { data } = await api.get<ScannedEntitySummary>(
      `/scan/${entityType}/${entityId}`,
    );
    return data;
  },

  qrSvgUrl: (
    entityType: ScannableEntityType,
    entityId: string,
    size?: number,
  ): string => {
    const sizeQs = size ? `&size=${size}` : '';
    return apiUrl(`/qr/${entityType}/${entityId}?format=svg${sizeQs}`);
  },

  qrPngUrl: (
    entityType: ScannableEntityType,
    entityId: string,
    size?: number,
  ): string => {
    const sizeQs = size ? `&size=${size}` : '';
    return apiUrl(`/qr/${entityType}/${entityId}?format=png${sizeQs}`);
  },

  /**
   * URL of the printable PDF label. Using `<embed>`/`<iframe>` or
   * window.open(url) will let the browser handle the print dialog
   * — no need to fetch the bytes ourselves.
   */
  labelPdfUrl: (
    entityType: ScannableEntityType,
    entityId: string,
    format: LabelFormat = 'dymo32x57',
  ): string => apiUrl(`/labels/${entityType}/${entityId}?format=${format}`),

  listFormats: async (): Promise<LabelFormatDescriptor[]> => {
    const { data } =
      await api.get<LabelFormatDescriptor[]>('/labels/formats');
    return data;
  },
};
